import type { FacilityCapability } from '@truecare/schemas';

export type FacilityWorkspaceTabKey = 'facility' | 'shifts' | 'invoices' | 'users';

/**
 * RBAC 3.2 — decide which facility /me workspace tabs a login sees, and which it
 * lands on. Pure so it's exhaustively testable without router/auth context.
 *
 * The backend capability guard is the real enforcement; this only avoids showing
 * a tab whose panel would 403. "Facility" (the org record) is always visible
 * (@AnyFacilityUser); Shifts needs SHIFT_VIEW; Invoices needs INVOICE_VIEW. A
 * Payer (INVOICE_VIEW, no SHIFT_VIEW) therefore sees Invoices but not Shifts.
 *
 * `defaultTabKey` lands on the first OPERATIONAL tab the login can use, falling
 * back to the always-present Facility record — and only defaults to Shifts when
 * the facility is active (a paused facility can't transact, so there's nothing
 * to do on Shifts).
 */
export function facilityWorkspaceTabPlan(
  capabilities: readonly FacilityCapability[],
  isActive: boolean,
): { visible: FacilityWorkspaceTabKey[]; defaultTabKey: FacilityWorkspaceTabKey } {
  const canViewShifts = capabilities.includes('SHIFT_VIEW');
  const canViewInvoices = capabilities.includes('INVOICE_VIEW');
  // Facility-user self-service — only an Admin (ACCOUNT_MANAGE) manages the team.
  const canManageUsers = capabilities.includes('ACCOUNT_MANAGE');

  const visible: FacilityWorkspaceTabKey[] = [
    'facility',
    ...(canViewShifts ? (['shifts'] as const) : []),
    ...(canViewInvoices ? (['invoices'] as const) : []),
    ...(canManageUsers ? (['users'] as const) : []),
  ];

  const defaultTabKey: FacilityWorkspaceTabKey =
    isActive && canViewShifts ? 'shifts' : canViewInvoices ? 'invoices' : 'facility';

  return { visible, defaultTabKey };
}
